import { useEffect } from "react";

import { useDynamicConfig, useExperiment } from "@statsig/react-bindings";

import { useStatsigUserContext } from "~/statsig/StatsigUserProvider";

import { useBookListingContext } from "../BookListingContext";
import {
  USE_ZERO_DOWN_EXPERIMENT_V1_CONFIG,
  ZERO_DOWN_EXPERIMENT,
} from "../experiment.consts";

/**
 * Encapsulates the zero down payment A/B experiment logic for bookings
 * 9–32 days out, exposes $0 down for 32+ day bookings when permitted, and
 * syncs the test bucket assignment back to the Statsig user profile.
 *
 * `use_zero_down_experiment_v1` dynamic config keeps v2 logic compatible with
 * v1 backend semantics for `payLaterTestBucket` until the backend ships.
 */
export const useZeroDownPayment = () => {
  const { quote } = useBookListingContext();
  const { setAttributes } = useStatsigUserContext();

  const zeroDownExperimentV1Config = useDynamicConfig(
    USE_ZERO_DOWN_EXPERIMENT_V1_CONFIG,
  );
  const isZeroDownV1BackendEnabled =
    zeroDownExperimentV1Config.value?.zeroDownExperimentFlag ?? false;

  const isZeroDownPaymentV2Variant = useExperiment(ZERO_DOWN_EXPERIMENT).get(
    "variant",
    false,
  );

  const payLaterPermitted = quote?.paymentSchedules?.pay_later?.permitted;
  const payLaterTestBucket = quote?.payLaterTestBucket ?? false;

  // v1 backend: payLaterTestBucket is true for 32+ day bookings.
  // v2 backend: payLaterTestBucket is false for 32+ day bookings.
  const shouldShowZeroDownPayment = Boolean(
    (!payLaterTestBucket || isZeroDownV1BackendEnabled) && payLaterPermitted,
  );

  const isZeroDownPayment =
    shouldShowZeroDownPayment ||
    Boolean(payLaterPermitted && isZeroDownPaymentV2Variant);

  const showZeroDownSkeleton =
    Boolean(shouldShowZeroDownPayment) || isZeroDownPaymentV2Variant;

  // Sync the pay-later bucket onto the Statsig user through the single writer.
  // `setAttributes` is idempotent and merge-based, so the two mount points
  // (BookListingForm + MobileBookListingSection) can't clobber each other or the
  // listing targeting attrs.
  useEffect(() => {
    if (!quote) return;

    setAttributes({ payLaterBucket: quote.payLaterTestBucket ?? false });
  }, [quote, setAttributes]);

  return {
    isZeroDownPaymentV2Variant,
    isZeroDownPayment,
    showZeroDownSkeleton,
  };
};
